package com.dotdrives.web.pages.settings

import com.dotdrives.controller.*
import com.dotdrives.design.DotButtonVariant
import kotlinx.browser.document
import com.dotdrives.model.*
import com.dotdrives.web.ui.*
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import web.cssom.ClassName
import web.dom.ElementId
import web.html.InputType
import web.html.email
import web.html.submit

external interface TeamWorkflowPanelProps : Props {
    var workflow: WorkflowController
    var workflowState: WorkflowState
    var state: SettingsUiState
    var role: String
}

val TeamWorkflowPanel =
    FC<TeamWorkflowPanelProps> { props ->
        val (inviteOpen, setInviteOpen) = useState(false)
        val (deliveryOpen, setDeliveryOpen) = useState(false)
        val (email, setEmail) = useState("")
        val (inviteRole, setInviteRole) = useState("member")
        val (submission, setSubmission) = useState<InviteSubmission?>(null)
        val wasInviteOpen = useRef(false)
        val (savedEmail, setSavedEmail) = useState("")
        val admin = canAdminister(props.role)
        val locked = props.workflowState.busy || props.workflowState.pending != null
        useEffect(inviteOpen) {
            if (inviteOpen) document.getElementById("settings-invite-email")?.asDynamic()?.focus()
            else if (wasInviteOpen.current == true) document.getElementById("settings-invite-open")?.asDynamic()?.focus()
            wasInviteOpen.current = inviteOpen
        }
        useEffect(submission, props.workflowState.busy, props.workflowState.pending, props.workflowState.result, props.workflowState.error) {
            val submitted = submission
            val current = props.workflowState
            if (submitted != null && !current.busy) {
                if (current.error != null && current.pending == null) setSubmission(null)
                else if (current.pending == null && current.result?.text("invite_id") == submitted.inviteId) {
                    setSavedEmail(submitted.email); setInviteOpen(false); setEmail(""); setInviteRole("member"); setSubmission(null)
                }
            }
        }
        if (admin) {
            div {
                className = ClassName("settings-section-card")
                div {
                    className = ClassName("settings-section-title")
                    +"Invitations"
                }
                if (savedEmail.isNotBlank()) div { asDynamic()["role"] = "status"; className = ClassName("settings-saved"); +"Invitation queued for $savedEmail." }
                if (!inviteOpen) button {
                    id = "settings-invite-open".unsafeCast<ElementId>()
                    className = ClassName("dot-button dot-button-primary")
                    disabled = locked
                    onClick = { setSavedEmail(""); setInviteOpen(true) }
                    +"Invite teammate"
                }
                if (inviteOpen)
                    form {
                        className = ClassName("settings-focused-form")

                        onKeyDown = { if (it.key == "Escape" && !locked) { it.preventDefault(); setSubmission(null); setInviteOpen(false); setEmail(""); setInviteRole("member") } }
                        onSubmit = {
                            it.preventDefault()
                            if (!locked) {
                                val intendedEmail = email.trim().lowercase()
                                val previousResult = props.workflow.state.value.result
                                props.workflow.perform("workflows", "invite.create", mapOf("email" to intendedEmail, "role" to inviteRole))
                                val current = props.workflow.state.value
                                val pending = current.pending?.takeIf { it.endpoint == "workflows" && it.body.text("action") == "invite.create" && it.body.text("email") == intendedEmail }
                                val inviteId = pending?.body?.text("invite_id")
                                    ?: current.result?.takeIf { it !== previousResult && current.error == null }?.text("invite_id")
                                setSubmission(inviteId?.let { InviteSubmission(it, intendedEmail) })
                            }
                        }
                        label {
                            +"Email"
                            input {
                                id = "settings-invite-email".unsafeCast<ElementId>()
                                disabled = locked
                                type = InputType.email
                                required = true
                                value = email
                                onChange = { setEmail(it.currentTarget.value) }
                            }
                        }
                        label {
                            +"Role"
                            select {
                                disabled = locked
                                value = inviteRole
                                onChange = { setInviteRole(it.currentTarget.value) }
                                INVITABLE_ROLES.forEach {
                                    option {
                                        value = it
                                        +roleLabel(it)
                                    }
                                }
                            }
                        }
                        DotButton {
                            text = "Cancel"; variant = DotButtonVariant.Secondary; enabled = !locked
                            onClick = { setInviteOpen(false); setEmail(""); setInviteRole("member"); setSubmission(null) }
                        }
                        button {
                            className = ClassName("dot-button dot-button-primary")
                            type = web.html.ButtonType.submit
                            disabled =
                                props.workflowState.busy || props.workflowState.pending != null
                            +"Send invitation"
                        }
                    }
                div {
                    className = ClassName("settings-section-subtitle")
                    +"Invitations are queued for email delivery. The recipient must sign in with this verified email address to accept."
                }
                props.state.invites.forEach { invite ->
                    val lifecycle =
                        when {
                            invite.revokedAt != null -> "Revoked"
                            invite.acceptedAt != null -> "Accepted"
                            (invite.expiresAt?.let { it < kotlin.time.Clock.System.now() } ==
                                true) -> "Expired"
                            else -> "Awaiting acceptance"
                        }
                    val latest =
                        props.workflowState.jobs
                            .filter { it.references(invite.id) }
                            .maxByOrNull { it.createdAt.orEmpty() }
                    div {
                        key = invite.id.unsafeCast<Key>()
                        className = ClassName("settings-invite-row")
                        div {
                            className = ClassName("settings-invite-copy")
                            div { className = ClassName("settings-invite-email"); +invite.email }
                            div { className = ClassName("settings-invite-meta"); +roleLabel(invite.role) }
                            latest?.let { div { className = ClassName("settings-invite-meta"); +it.label } }
                        }
                        div { className = ClassName("settings-status-pill"); +lifecycle }
                        if (lifecycle == "Awaiting acceptance")
                            DotButton {
                                text = "Resend"
                                variant = DotButtonVariant.Secondary
                                enabled = !locked
                                onClick = {
                                    props.workflow.perform(
                                        "workflows",
                                        "invite.resend",
                                        mapOf("invite_id" to invite.id),
                                    )
                                }
                            }
                        if (invite.revokedAt == null && invite.acceptedAt == null)
                            DotButton {
                                text = "Revoke"
                                variant = DotButtonVariant.Secondary
                                enabled = !locked
                                onClick = {
                                    props.workflow.perform(
                                        "workflows",
                                        "invite.revoke",
                                        mapOf("invite_id" to invite.id),
                                    )
                                }
                            }
                    }
                }
            }
            DotButton {
                variant = DotButtonVariant.Secondary
                text =
                    if (deliveryOpen) "Hide delivery history"
                    else "View invitation delivery history"
                onClick = { setDeliveryOpen(!deliveryOpen) }
            }
            if (deliveryOpen)
                DeliveryJobs {
                    controller = props.workflow
                    state = props.workflowState
                    sourceId = null
                    kinds = listOf("email.invite")
                }
        } else
            div {
                className = ClassName("settings-section-subtitle")
                +"An owner or administrator manages invitations and teammate access."
            }
    }

private data class InviteSubmission(val inviteId: String, val email: String)
